import {useState} from "react"
import type {OtkBlank} from "shared"
import type {EUSignLibraryAdapter} from "iit/browser"

import {Button} from "@/components/ui/button"
import {Checkbox} from "@/components/ui/checkbox"
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,} from "@/components/ui/dialog"
import {Field, FieldDescription, FieldGroup, FieldLabel,} from "@/components/ui/field"
import {Textarea} from "@/components/ui/textarea"
import {ScenarioNotice} from "@/components/ScenarioNotice"
import {KepKeyReader} from "@/components/kep/KepKeyReader"
import {ActRequisitesFields, TerritorialBodyField,} from "@/components/warehouse/ActRequisitesFields"
import {ActPrintSheet} from "@/components/warehouse/ActPrintSheet"
import {type ActRequisitesInput, actRequisitesMissing, emptyActRequisites,} from "@/lib/actRequisites"
import {
    ATTESTATION_STATEMENT,
    buildCreateDestructionActPayload,
    COPY_DUTY_STATEMENT,
    type DestructionActMode,
    MARK_JOURNAL_STATEMENT,
} from "@/lib/destructionAct"
import {blankStatusLabels, formatUaDate} from "@/lib/warehouse"

/**
 * warehouse.md's canonical `DestructionActDialog` — one component for both
 * paths to `ЗНИЩЕНО` (mode `annulled` / `availableDamaged`), ported from the
 * wireframe field-for-field: order, both legal-basis notices, the single
 * shared-reason textarea, both stored statements, `ActRequisitesFields` +
 * `TerritorialBodyField`, the copy-duty checkbox, the unmet-requisite line,
 * the refusal notice, and the print footer button with its hidden
 * `ActPrintSheet` sibling. One deliberate deviation from the page spec: the
 * `KepKeyReader` finalizer is unconditional on both modes, not just
 * `availableDamaged` — see the comment on `finalize` below for why.
 */
export function DestructionActDialog({
  blanks,
  mode,
  onFinalize,
  onOpenChange,
  open,
  subjectName,
  userName,
}: {
  blanks: OtkBlank[]
  mode: DestructionActMode
  /**
   * Sends the exact JSON bytes signed by КЕП as the request body, with the
   * detached signature in X-KEP-Signature.
   */
  onFinalize: (
    signedPayload: string,
    kepSignature: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  onOpenChange: (open: boolean) => void
  open: boolean
  subjectName: string
  userName: string
}) {
  const availableDamaged = mode === "availableDamaged"
  const [reason, setReason] = useState("")
  const [attested, setAttested] = useState(false)
  const [markAcknowledged, setMarkAcknowledged] = useState(false)
  const [copyAcknowledged, setCopyAcknowledged] = useState(false)
  const [req, setReq] = useState<ActRequisitesInput>(emptyActRequisites())
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // warehouse.md's own "залишилось заповнити" composition, ported verbatim
  // from the wireframe: the reason/attestation/mark-journal/copy-ack items
  // are this dialog's own state, `actRequisitesMissing` covers the form-244
  // fields, and the territorial-body text gets its own line since
  // `TerritorialBodyField` isn't part of that helper.
  const missing: string[] = []
  if (!reason.trim()) missing.push("причина знищення")
  if (!attested) missing.push("підтвердження фактичного огляду")
  if (availableDamaged && !markAcknowledged) {
    missing.push("підтвердження відмітки «Анульовано» і запису в журналі")
  }
  missing.push(...actRequisitesMissing(req))
  if (!req.territorialBody.trim()) {
    missing.push("територіальний орган — отримувач копії")
  }
  if (!copyAcknowledged) missing.push("підтвердження обов'язку надіслати копію")
  const canFinalize = blanks.length > 0 && missing.length === 0

  const previewBlankReasons = Object.fromEntries(blanks.map((b) => [b.id, reason]))

  // The real backend has exactly one creation endpoint, and it always
  // requires a КЕП-signed payload —
  // unlike warehouse.md's own mode split, where `annulled` kept a plain,
  // unsigned finalizer ("Сформувати акт і знищити (N)") and only
  // `availableDamaged` signed. There is no unsigned creation path to call
  // instead, so this dialog signs in both modes; the button label still
  // reflects the mode's own legal act (act composition vs. destruction).
  async function finalize(adapter: EUSignLibraryAdapter) {
    if (!canFinalize || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const payload = buildCreateDestructionActPayload({
        mode,
        blanks,
        reason: reason.trim(),
        requisites: req,
        territorialBody: req.territorialBody.trim(),
      })
      const signedPayload = JSON.stringify(payload)
      const kepSignature = await adapter.sign(new TextEncoder().encode(signedPayload))
      const result = await onFinalize(signedPayload, kepSignature)
      if (!result.ok) {
        setSubmitError(result.message)
        return
      }
      onOpenChange(false)
    } catch {
      setSubmitError("Не вдалося підписати акт КЕП. Перевірте носій ключа і спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {/* No act-number preview: the real number is assigned
                server-side, atomically with the signed submission, so it
                isn't known before a successful finalize (unlike the
                wireframe's own optimistic local counter). */}
            {availableDamaged
              ? "Знищення пошкоджених бланків — новий акт"
              : "Акт знищення бланків протоколів — новий акт"}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          <ScenarioNotice tone="default">
            {availableDamaged ? (
              <>
                Підстава — п. 12 Постанови КМУ від 08.02.2012 № 146: на
                пошкодженому або зіпсованому бланку ставиться відмітка
                «Анульовано», відомості вносяться до загальнодержавної бази
                даних (з КЕП — п. 3 Постанови КМУ № 512) і журналу обліку,
                після чого бланк знищується за актом за зразком наказу МВС від
                04.04.2016 № 244 з копією територіальному органу. Ануляція
                протоколу не потрібна — документів за цими бланками немає. Усе
                оформлюється тут однією дією для {subjectName}.
              </>
            ) : (
              <>
                Акт складається за зразком, затвердженим наказом МВС від
                04.04.2016 № 244, для {subjectName}. Підстава знищення — п. 12
                Постанови КМУ від 08.02.2012 № 146: пошкоджені або зіпсовані
                бланки.
              </>
            )}
          </ScenarioNotice>
          <FieldGroup>
            <div>
              <p className="mb-2 text-sm font-medium">
                Бланки до знищення ({blanks.length})
              </p>
              <div className="max-h-64 overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/60">
                    <tr>
                      <th className="p-2 text-left font-medium">
                        Серія і номер
                      </th>
                      <th className="p-2 text-left font-medium">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blanks.map((b) => (
                      <tr className="border-t" key={b.id}>
                        <td className="p-2 font-medium whitespace-nowrap">
                          {b.series} {b.number}
                        </td>
                        <td className="p-2 whitespace-nowrap text-muted-foreground">
                          {blankStatusLabels[b.status]} · {formatUaDate(b.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <FieldDescription className="mt-2">
                До офіційного акта до кожного рядка вклеюється вирізаний з
                бланка номер. Цю дію АРМ не замінює й не фіксує.
              </FieldDescription>
            </div>

            <Field>
              <FieldLabel htmlFor="destr-reason">
                Причина знищення для всіх обраних бланків
              </FieldLabel>
              <Textarea
                id="destr-reason"
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  availableDamaged
                    ? "напр., пошкоджено під час друку — зім'ято принтером"
                    : "напр., пошкоджено при зберіганні"
                }
                rows={3}
                value={reason}
              />
              <FieldDescription>
                Одна причина на все ({blanks.length}): у друкованій формі й у
                збереженому акті вона стоїть у рядку кожного бланка, і той
                самий текст лягає в історію кожного з них.
              </FieldDescription>
            </Field>

            <ScenarioNotice tone="warning">
              <label className="flex items-start gap-3">
                <Checkbox
                  checked={attested}
                  className="mt-0.5"
                  onCheckedChange={(v) => setAttested(v === true)}
                />
                <span className="text-sm">{ATTESTATION_STATEMENT}</span>
              </label>
            </ScenarioNotice>

            {availableDamaged && (
              <label className="flex items-start gap-3">
                <Checkbox
                  checked={markAcknowledged}
                  className="mt-0.5"
                  onCheckedChange={(v) => setMarkAcknowledged(v === true)}
                />
                <span className="text-sm">{MARK_JOURNAL_STATEMENT}</span>
              </label>
            )}

            <p className="text-sm font-medium">Реквізити акта (форма № 244)</p>
            <ActRequisitesFields onChange={setReq} value={req} />
            <TerritorialBodyField
              onChange={(territorialBody) => setReq((r) => ({ ...r, territorialBody }))}
              value={req.territorialBody}
            />

            <label className="flex items-start gap-3">
              <Checkbox
                checked={copyAcknowledged}
                className="mt-0.5"
                onCheckedChange={(v) => setCopyAcknowledged(v === true)}
              />
              <span className="text-sm">{COPY_DUTY_STATEMENT}</span>
            </label>

            <KepKeyReader
              actionLabel={
                availableDamaged
                  ? "Підписати КЕП і завершити знищення"
                  : "Підписати КЕП і сформувати акт"
              }
              disabled={!canFinalize}
              onRead={({ adapter }) => void finalize(adapter)}
            />

            {!canFinalize && blanks.length > 0 && (
              <FieldDescription>
                Для {availableDamaged ? "завершення" : "формування акта"}{" "}
                залишилось заповнити: {missing.join("; ")}.
              </FieldDescription>
            )}

            {submitError && (
              <ScenarioNotice tone="danger">{submitError}</ScenarioNotice>
            )}
          </FieldGroup>
        </div>
        <DialogFooter className="sm:justify-start">
          <Button onClick={() => window.print()} variant="outline">
            Друкувати / Зберегти PDF
          </Button>
        </DialogFooter>
      </DialogContent>
      {open && (
        <ActPrintSheet
          actDate={req.actDate}
          attestationStatement={attested ? ATTESTATION_STATEMENT : undefined}
          attestedByUser={attested ? userName : undefined}
          blankReasons={previewBlankReasons}
          blanks={blanks}
          commissionChair={req.chair}
          commissionMembers={req.members.filter((m) => m.role.trim() || m.name.trim())}
          dateFrom={req.dateFrom}
          dateTo={req.dateTo}
          destructionMethod={req.destructionMethod}
          destructionPlace={req.destructionPlace}
          destructionTime={req.destructionTime}
          location={req.location}
          territorialBody={req.territorialBody}
        />
      )}
    </Dialog>
  )
}
